import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { AssignDepositorForm } from './ui/AssignDepositorForm/AssignDepositorForm';
import { InboundWireEditForm } from '@/bundle/_OrgAdmin/pages/InboundWire/_InboundWireEdit/InboundWireEditForm';
import { SelectDepositAccountForm } from './ui/SelectDepositAccountForm/SelectDepositAccountForm';
import { PageContent } from '@/components/Page/PageContent/PageContent';
import { PageColumn } from '@/components/Page/PageColumn/PageColumn';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInterval } from '@/hooks/useInterval';
import { isCreatedWireStatus, isDepositorUnassignedWireStatus, isPendingWireStatus } from '@/helpers/wireHelpers';
import { PaymentDetails } from './ui/PaymentDetails/PaymentDetails';
import { EditUserForm } from '@/bundle/shared/components/EditUserForm/EditUserForm';
import { USER_ROLE_MAP } from '@/const/user';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { NOT_ALLOWED_WIRE_STATUSES_CANCEL_INBOUND } from '@/bundle/_OrgAdmin/shared/const/const';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { OrgAdminWireDetails } from '@/bundle/_OrgAdmin/shared/ui/OrgAdminWireDetails/OrgAdminWireDetails';
import { useGetCurrencyOptions } from '@/bundle/shared/hooks/useGetCurrencyOptions';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { OrganizationSubscriptionStatusBanner } from '@/bundle/shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { useUser } from '@/context/userContext';
import { isPaymentsLimitReached } from '@/helpers/billingHelpers';
import { PaymentsLimitReachedWidget } from '@/bundle/_OrgAdmin/shared/ui/PaymentLimitReachedWidget/PaymentsLimitReachedWidget';
import { CancelWire } from './ui/CancelWire/CancelWire';
import { getTransactionDetailsPath } from '../TransactionDetailsPage/path/path';
import {
  loadDepositAccountPaymentDetails,
  loadInboundWire,
  loadPaymentDetails,
  updateWirePaymentDetails,
} from '@/bundle/_OrgAdmin/pages/InboundWire/api/api';
import { UpdateWireDepositAccountPaymentDetailsPayloadType } from '@/api/v1/organization/deposit-accounts/updateWireDepositAccountPaymentDetailsApi';
import { EditWireDetails } from '../OutboundWire/_OutboundWireEdit/ui/EditWireDetails/EditWireDetails';
import { DepositorUserDetails } from './ui/DepositorUserDetails/DepositorUserDetails';
import { LoadPaymentDetailsLoading } from '../OutboundWire/ui/LoadPaymentDetailsWidget/LoadPaymentDetailsLoading';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const InboundWireDetailsPage = () => {
  const { user } = useUser();
  const { transactionId, id } = useParams<{ transactionId: string; id: string }>();
  const queryClient = useQueryClient();
  const { navigateBack } = useStatefulNavigation();
  const { isInactiveSubscription } = useSubscriptionStatus();

  const [isEditInboundWireWidget, setIsEditInboundWireWidget] = useState(false);
  const [isOpenSendModal, setIsOpenSendModal] = useState(false);
  const [isEditDepositor, setIsEditDepositor] = useState(false);
  const [selectedAccountOption, setSelectedAccountOption] = useState<{ label: string; value: string }>();

  const refetchInboundWire = () => {
    queryClient.invalidateQueries({ queryKey: ['inbound_wire', id] });
  };

  const {
    data: inboundWireData,
    isPending: isPendingInboundWire,
    isFetching: isFetchingInboundWire,
  } = useQuery({ queryKey: ['inbound_wire', id], queryFn: () => loadInboundWire(id) });
  const inboundWire = inboundWireData?.body;

  const { data: selectedDepositAccountData, isFetching: isFetchingDepositAccountPaymentDetails } = useQuery({
    queryKey: ['load_deposit_account_payment_details', selectedAccountOption],
    queryFn: () => loadDepositAccountPaymentDetails(selectedAccountOption?.value),
    enabled: !!selectedAccountOption,
    placeholderData: keepPreviousData,
  });

  const { currencyOptions } = useGetCurrencyOptions(!!isEditInboundWireWidget);

  const { data: paymentDetailsData, isFetching: isFetchingPaymentDetails } = useQuery({
    queryKey: ['load_payment_details', id],
    queryFn: () => loadPaymentDetails(id),
    enabled: !!inboundWireData?.body?.is_payment_details_exist,
  });

  const {
    data: updatedPaymentDetails,
    mutate: mutateUpdatePaymentDetails,
    isPending: isPendingUpdatePaymentDetails,
  } = useMutation({
    mutationKey: ['update_payment_details'],
    mutationFn: (payload: UpdateWireDepositAccountPaymentDetailsPayloadType) =>
      updateWirePaymentDetails(id, selectedAccountOption.value, payload),
    onSuccess(response) {
      if (response?.error) return;

      amplitudeService.logEvent(AMPLITUDE_EVENTS.SelectPaymentDetailsInboundWireSuccess);

      setSelectedAccountOption(null);
      queryClient.setQueriesData({ queryKey: ['load_deposit_account_payment_details'] }, null);
      refetchInboundWire();
    },
  });

  const selectedDepositAccount = selectedDepositAccountData?.body;
  const paymentDetails = paymentDetailsData?.body;
  const wireStatus = inboundWire?.status;

  const isCreatedWire = isCreatedWireStatus(wireStatus);
  const isDepositorUnassignedWire = isDepositorUnassignedWireStatus(wireStatus);
  const isPendingStatus = isPendingWireStatus(wireStatus);

  const shouldSelectDepositAccount = isCreatedWire && !isFetchingInboundWire && !isPendingUpdatePaymentDetails;
  const showPaymentDetailsLoader = isFetchingPaymentDetails && !!inboundWire?.assigned_depositor;
  const assignedDepositor = inboundWire?.assigned_depositor?.user;
  const isAllowedToCancelWire =
    !!wireStatus && !NOT_ALLOWED_WIRE_STATUSES_CANCEL_INBOUND.includes(wireStatus) && !isPendingInboundWire;
  const paymentsLimitReached = isPaymentsLimitReached(user);
  const showBanner = isInactiveSubscription && isDepositorUnassignedWire;

  const backToWireList = () => navigateBack(getTransactionDetailsPath(transactionId));

  const openEditMode = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.EditGeneralInfoInboundWireRedirect);

    setIsEditInboundWireWidget(true);
  };

  const closeEditMode = () => setIsEditInboundWireWidget(false);

  const updateInboundWireSuccess = () => {
    closeEditMode();
  };

  const assignedDepositorHandler = () => {
    refetchInboundWire();

    queryClient.invalidateQueries({ queryKey: ['load_payment_details', id] });
    setIsOpenSendModal(false);
    setIsEditDepositor(false);
  };

  const { cancelInterval, isIntervalRunning } = useInterval(() => {
    if (isPendingStatus) {
      refetchInboundWire();
    }
  });

  const selectDepositAccount = (option: { label: string; value: string }) => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.PaymentDetailsSelectRedirect);

    setSelectedAccountOption(option);
  };

  useEffect(() => {
    const shouldCancelInterval = inboundWire && !isPendingStatus && isIntervalRunning;

    if (shouldCancelInterval) {
      cancelInterval();
    }
  }, [wireStatus, cancelInterval, isIntervalRunning, isPendingStatus, inboundWire]);

  return (
    <ContentLayout
      title='Wire details'
      breadcrumbs='Transactions > Details > Wire Details'
      headerAction={isAllowedToCancelWire && <CancelWire id={id} />}
      backButton='Back to Transaction'
      onBack={backToWireList}
      apiError={inboundWireData?.error}
      banner={showBanner && <OrganizationSubscriptionStatusBanner />}
    >
      <PageBody>
        <PageContent>
          <PageColumn>
            {isEditInboundWireWidget ? (
              <DetailsWidget header='Edit Wire' hasHighlight>
                <EditWireDetails status={wireStatus} dateUpdate={inboundWire?.date_modified} />
                <InboundWireEditForm
                  inboundWire={inboundWire}
                  currencyOptions={currencyOptions}
                  onClose={closeEditMode}
                  onUpdate={updateInboundWireSuccess}
                />
              </DetailsWidget>
            ) : (
              inboundWire && <OrgAdminWireDetails wire={inboundWire} onEdit={openEditMode} />
            )}
            {assignedDepositor && !isEditDepositor && (
              <DepositorUserDetails
                inboundWire={inboundWire}
                onRefetchWire={refetchInboundWire}
                onEdit={() => setIsEditDepositor(true)}
              />
            )}
          </PageColumn>
          <PageColumn>
            {isDepositorUnassignedWire && paymentsLimitReached && <PaymentsLimitReachedWidget limitType='deposit' />}
            {isDepositorUnassignedWire && !paymentsLimitReached && (
              <AssignDepositorForm
                wireId={id}
                isOpenConfirmModal={isOpenSendModal}
                onToggleConfirmModal={setIsOpenSendModal}
                onSuccess={assignedDepositorHandler}
              />
            )}
            {isEditDepositor && (
              <EditUserForm
                header='Edit Depositor'
                role={USER_ROLE_MAP.DEPOSITOR}
                user={inboundWire?.assigned_depositor?.user}
                wireId={inboundWire?.id}
                onClose={() => setIsEditDepositor(false)}
                onSuccess={refetchInboundWire}
              />
            )}
            {shouldSelectDepositAccount && (
              <SelectDepositAccountForm
                selectedDepositAccount={selectedDepositAccount}
                onChange={selectDepositAccount}
                isLoading={isFetchingDepositAccountPaymentDetails}
                selectedDepositAccountId={selectedAccountOption?.value}
                error={updatedPaymentDetails?.error}
                isPaymentDetailsUpdating={isPendingUpdatePaymentDetails}
                onCreate={mutateUpdatePaymentDetails}
              />
            )}
            {showPaymentDetailsLoader && (
              <DetailsWidget header='Wire Info'>
                <LoadPaymentDetailsLoading />
              </DetailsWidget>
            )}
            {paymentDetails && (
              <PaymentDetails paymentDetails={paymentDetails} wire={inboundWire} onResetSuccess={refetchInboundWire} />
            )}
          </PageColumn>
        </PageContent>
      </PageBody>
    </ContentLayout>
  );
};
